// @ts-strict-ignore
import { debug } from './debug';

export type ChangeCallback = (key: string | null, value: any) => void;

export interface TargetingObject {
	[key: string]: any;
}

export interface SlotTargeting {
	amznbid?: string;
	/* This value is derived from the A9Targeting interface and gets inserted into the targeting object.
	 * This is added in here to make it clear that this value exists and gets sent as a key-value in GAM requests.
	 * */
	amznp?: string;
	hb_bidder?: string;
	hb_pb?: string;
	src?: string;
	pos?: string;
	loc?: string;
	rv?: string | string[] | number;

	[key: string]: googletag.NamedSize | number;
}

export interface TargetingData {
	AU_SEG?: string[];
	adt?: string;
	age?: string[];
	alc?: string;
	ar?: string;
	artid?: string;
	b_ias?: string;
	bundles?: string[];
	dlm?: string;
	dmn?: string;
	drg?: string;
	experiment_groups?: string[];
	fr?: string;
	geo?: string;
	gnre?: string[];
	hat?: string;
	host?: string;
	hostpre?: string;
	'ias-kw'?: string[];
	is_mobile?: string;
	kid_wiki?: string;
	labrador?: string[];
	lang?: string;
	mappedVerticalName?: string;
	media?: string[];
	npa?: string;
	off?: string;
	original_host?: string;
	post_id?: string;
	ppid?: string;
	pv?: string;
	pvg?: string;
	rating?: any;
	rdp?: string;
	s0?: string;
	s0c?: string[];
	s0v?: string;
	s1?: string;
	s2?: string;
	skin?: string;
	theme?: string[];
	top?: string;
	tv?: string[];
	uap?: string;
	uap_c?: string;
	vio?: string;
	word_count?: number;
	wikiLanguage?: string;
	wpage?: string;
}

export interface TargetingServiceInterface {
	clear(slotName: string | null): void;

	extend(newTargeting: TargetingObject, slotName: string | null): void;

	dump<T = TargetingObject>(slotName: string | null): T;

	get(key: string, slotName: string | null): any;

	set(key: string, value: any, slotName: string | null): void;

	remove(key: string, slotName: string | null): void;

	onChange(callback: ChangeCallback): void;

	removeListeners(): void;
}

export class TargetingService implements TargetingServiceInterface {
	private adTargeting: TargetingObject = {};
	private onChangeCallbacks: ChangeCallback[] = [];
	private pageTargetingGroupName = 'PAGE_TARGETING';

	private sentryEnabled = false;
	private sentryKey = 'fs';
	private sentryValue = 't';

	constructor() {
		if (debug.isDebugMode()) {
			window.ads.adTargeting = this.adTargeting;
		}
	}

	clear(slotName: string | null = null): void {
		const targetingGroupName = slotName || this.pageTargetingGroupName;
		this.adTargeting[targetingGroupName] = {} as TargetingObject;
	}

	extend(newTargeting: TargetingObject, slotName: string | null = null): void {
		const targetingGroupName = slotName || this.pageTargetingGroupName;
		this.adTargeting[targetingGroupName] =
			this.adTargeting[targetingGroupName] || ({} as TargetingObject);
		this.adTargeting[targetingGroupName] = Object.assign(
			this.adTargeting[targetingGroupName],
			newTargeting,
		);

		this.ensureSentryIsLast(targetingGroupName);
	}

	dump<T = TargetingObject>(slotName: string | null = null): T {
		const targetingGroupName = slotName || this.pageTargetingGroupName;
		this.adTargeting[targetingGroupName] =
			this.adTargeting[targetingGroupName] || ({} as TargetingObject);

		this.ensureSentryIsLast(targetingGroupName);

		return this.adTargeting[targetingGroupName] as T;
	}

	get(key: string, slotName: string | null = null): any {
		const targetingGroupName = slotName || this.pageTargetingGroupName;

		if (this.adTargeting[targetingGroupName]) {
			this.ensureSentryIsLast(targetingGroupName);

			return this.adTargeting[targetingGroupName][key];
		}

		return undefined;
	}

	set(key: string, value: any, slotName: string | null = null): void {
		if (value !== undefined) {
			const targetingGroupName = slotName || this.pageTargetingGroupName;
			this.adTargeting[targetingGroupName] =
				this.adTargeting[targetingGroupName] || ({} as TargetingObject);
			this.adTargeting[targetingGroupName][key] = value;

			if (targetingGroupName === this.pageTargetingGroupName) {
				this.triggerOnChange(key, value);
			}

			this.ensureSentryIsLast(targetingGroupName);
		}
	}

	remove(key: string, slotName: string | null = null): void {
		const targetingGroupName = slotName || this.pageTargetingGroupName;

		if (this.adTargeting[targetingGroupName] && this.adTargeting[targetingGroupName][key]) {
			delete this.adTargeting[targetingGroupName][key];

			if (targetingGroupName === this.pageTargetingGroupName) {
				this.triggerOnChange(key, null);
			}

			this.ensureSentryIsLast(targetingGroupName);
		}
	}

	onChange(callback: ChangeCallback): void {
		this.onChangeCallbacks = this.onChangeCallbacks || [];
		this.onChangeCallbacks.push(callback);
	}

	removeListeners(): void {
		this.onChangeCallbacks = [];
	}

	private triggerOnChange(key: string, newValue: any): void {
		this.onChangeCallbacks.forEach((callback) => {
			callback(key, newValue);
		});
	}

	addSentryFlag(flag: boolean): void {
		this.sentryEnabled = flag;
	}

	private ensureSentryIsLast(targetingGroupName: string) {
		if (
			this.sentryEnabled &&
			this.adTargeting[targetingGroupName] &&
			Object.keys(this.adTargeting[targetingGroupName] || {}).length > 0
		) {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { [this.sentryKey]: _unused, ...rest } = this.adTargeting[targetingGroupName];
			this.adTargeting[targetingGroupName] = { ...rest, [this.sentryKey]: this.sentryValue };
		}
	}
}

export const targetingService = new TargetingService();
